import { setupRcdPhoneInputs } from "../core/components/phone-input";
import { openRcdDialog, closeRcdDialog } from "../core/components/dialog";
import { setupRcdDropdownKits } from "../core/components/dropdown-kit";
import { renderRcdIcons } from "../core/icons";
import { createRcdThemeState } from "../core/theme";

const setupWorkspaceSettingsPage = () => {
    const root = document.querySelector("[data-settings-root]");
    if (!root || root.dataset.settingsReady === "true") {
        return;
    }

    root.dataset.settingsReady = "true";

    const setActiveSection = (sectionId, { push = true } = {}) => {
        const nextSection = sectionId || "billing-rates";
        root.dataset.activeSection = nextSection;

        root.querySelectorAll("[data-settings-section-link]").forEach((link) => {
            const isActive = link.dataset.settingsSectionLink === nextSection;
            link.classList.toggle("is-active", isActive);
            link.setAttribute("aria-selected", isActive ? "true" : "false");
        });

        root.querySelectorAll("[data-settings-panel]").forEach((panel) => {
            panel.classList.toggle("is-active", panel.dataset.settingsPanel === nextSection);
        });

        if (push) {
            const url = new URL(window.location.href);
            url.pathname = "/Settings";
            url.searchParams.set("section", nextSection);
            window.history.pushState({ settingsSection: nextSection }, "", url);
        }

        renderRcdIcons();
    };

    const refreshRoot = async (target, { push = true } = {}) => {
        const url = target && (String(target).startsWith("/") || String(target).startsWith("?") || String(target).startsWith("http"))
            ? new URL(target, window.location.origin)
            : new URL(window.location.href);
        url.pathname = "/Settings";
        if (!String(target || "").startsWith("?") && !String(target || "").startsWith("/") && !String(target || "").startsWith("http")) {
            url.searchParams.set("section", target || root.dataset.activeSection || "billing-rates");
        }

        root.classList.add("is-loading");
        root.setAttribute("aria-busy", "true");

        const response = await fetch(url, {
            credentials: "same-origin",
            headers: {
                "Accept": "text/html",
                "X-Requested-With": "XMLHttpRequest"
            }
        });

        if (!response.ok) {
            throw new Error("Settings could not be refreshed.");
        }

        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const nextRoot = doc.querySelector("[data-settings-root]");
        if (!nextRoot) {
            throw new Error("Settings response was incomplete.");
        }

        root.replaceWith(nextRoot);
        if (push) {
            window.history.pushState({ settingsSection: nextRoot.dataset.activeSection || url.searchParams.get("section") || "billing-rates" }, "", url);
        }
        window.Alpine?.initTree?.(nextRoot);
        renderRcdIcons();
        setupRcdDropdownKits(nextRoot, renderRcdIcons);
        setupWorkspaceSettingsPage();
        setupRcdPhoneInputs();
    };

    const priceListModal = root.querySelector("[data-price-list-modal]");
    const priceListForm = root.querySelector("[data-price-list-form]");
    const priceListCategoryModal = root.querySelector("[data-price-list-category-modal]");
    const priceListCategoryForm = root.querySelector("[data-price-list-category-form]");
    const setPriceListField = (name, value) => {
        const field = priceListForm?.querySelector(`[data-price-list-field="${name}"]`);
        if (!field) return;
        if (field.type === "checkbox") field.checked = value === true || value === "true";
        else field.value = value ?? "";
        if (field.tagName === "SELECT") field.dispatchEvent(new Event("change", { bubbles: true }));
    };
    const updatePriceListSupplier = () => {
        const purchased = priceListForm?.querySelector('[data-price-list-field="purchased"]')?.checked === true;
        const supplierField = priceListForm?.querySelector("[data-price-list-supplier-field]");
        const supplier = priceListForm?.querySelector('[data-price-list-field="supplier"]');
        if (supplierField) supplierField.hidden = !purchased;
        if (supplier) supplier.required = purchased;
    };
    const updatePriceListLevels = (source = null) => {
        const method = priceListForm?.querySelector('[data-price-list-field="pricingMethod"]')?.value || "BuyPriceMarkup";
        const cost = Number(priceListForm?.querySelector('[data-price-list-field="cost"]')?.value || 0);
        let fallbackSell = 0;
        let defaultSell = null;
        priceListForm?.querySelectorAll("[data-price-list-level-row]").forEach((row) => {
            const markupInput = row.querySelector("[data-price-list-level-markup]");
            const sellInput = row.querySelector("[data-price-list-level-sell]");
            if (!markupInput || !sellInput) return;
            if (method === "BuyPriceMarkup") {
                markupInput.readOnly = true;
                sellInput.readOnly = true;
                const markup = Number(markupInput.value || row.dataset.priceListDefaultMarkup || 0);
                sellInput.value = (cost * (1 + markup / 100)).toFixed(4);
            } else if (method === "Manual") {
                markupInput.readOnly = true;
                sellInput.readOnly = false;
                const sell = Number(sellInput.value || 0);
                markupInput.value = cost > 0 ? (((sell / cost) - 1) * 100).toFixed(4) : "0.0000";
            } else {
                markupInput.readOnly = true;
                sellInput.readOnly = true;
                sellInput.value = "0.0000";
            }
            const isDefault = row.querySelector('input[name$=".IsDefault"]')?.value?.toLowerCase() === "true";
            if (fallbackSell === 0) fallbackSell = Number(sellInput.value || 0);
            if (isDefault) defaultSell = Number(sellInput.value || 0);
        });
        const legacySell = priceListForm?.querySelector('[data-price-list-field="sell"]');
        if (legacySell) legacySell.value = Number(defaultSell ?? fallbackSell).toFixed(4);
    };
    const openPriceListModal = (trigger) => {
        if (!priceListModal || !priceListForm) return;
        priceListForm.reset();
        ["id", "code", "name", "description", "type", "unit", "supplier", "cost", "sell", "tax"].forEach((name) => setPriceListField(name, trigger?.dataset[`priceList${name[0].toUpperCase()}${name.slice(1)}`] || ""));
        setPriceListField("type", trigger?.dataset.priceListType || "Material");
        setPriceListField("unit", trigger?.dataset.priceListUnit || "each");
        setPriceListField("purchased", trigger?.dataset.priceListPurchased || "false");
        setPriceListField("includesTax", trigger?.dataset.priceListIncludesTax || "false");
        setPriceListField("pricingMethod", trigger?.dataset.priceListPricingMethod || "BuyPriceMarkup");
        setPriceListField("categoryId", trigger?.dataset.priceListCategoryId || "");
        setPriceListField("archived", trigger?.dataset.priceListArchived || "false");
        let prices = [];
        try { prices = JSON.parse(trigger?.dataset.priceListPrices || "[]"); } catch { prices = []; }
        priceListForm.querySelectorAll("[data-price-list-level-row]").forEach((row) => {
            const price = prices.find((item) => String(item.PricingLevelId || item.pricingLevelId) === row.dataset.priceListLevelId);
            const markup = row.querySelector("[data-price-list-level-markup]");
            const sell = row.querySelector("[data-price-list-level-sell]");
            if (markup) markup.value = price ? (price.MarkupPercent ?? price.markupPercent ?? 0) : (row.dataset.priceListDefaultMarkup || 0);
            if (sell) sell.value = price ? (price.SellPrice ?? price.sellPrice ?? 0) : 0;
        });
        updatePriceListSupplier();
        updatePriceListLevels();
        openRcdDialog(priceListModal, trigger);
        renderRcdIcons();
    };
    const closePriceListModal = () => priceListModal && closeRcdDialog(priceListModal);
    const closePriceListCategoryModal = () => priceListCategoryModal && closeRcdDialog(priceListCategoryModal);
    priceListForm?.addEventListener("input", (event) => {
        if (event.target.matches('[data-price-list-field="cost"], [data-price-list-level-sell], [data-price-list-level-markup]')) updatePriceListLevels(event.target);
    });
    priceListForm?.addEventListener("change", (event) => {
        if (event.target.matches('[data-price-list-field="purchased"]')) updatePriceListSupplier();
        if (event.target.matches('[data-price-list-field="pricingMethod"]')) updatePriceListLevels(event.target);
    });

    root.querySelectorAll("[data-price-list-tab]").forEach((tab) => tab.addEventListener("click", () => {
        const target = tab.dataset.priceListTab;
        root.querySelectorAll("[data-price-list-tab]").forEach((item) => {
            const active = item === tab;
            item.classList.toggle("is-active", active);
            item.setAttribute("aria-selected", active ? "true" : "false");
        });
        root.querySelectorAll("[data-price-list-view]").forEach((view) => { view.hidden = view.dataset.priceListView !== target; });
    }));
    const filterPriceList = () => {
        const query = (root.querySelector("[data-price-list-search]")?.value || "").trim().toLocaleLowerCase();
        const showArchived = root.querySelector("[data-price-list-archived]")?.checked === true;
        root.querySelectorAll("[data-price-list-row]").forEach((row) => {
            const matches = !query || (row.dataset.priceListText || "").toLocaleLowerCase().includes(query);
            row.hidden = !matches || (!showArchived && row.dataset.priceListActive !== "true");
        });
    };
    root.querySelector("[data-price-list-search]")?.addEventListener("input", filterPriceList);
    root.querySelector("[data-price-list-archived]")?.addEventListener("change", filterPriceList);
    const priceListImportInput = root.querySelector("[data-price-list-import-input]");
    root.querySelector("[data-price-list-import-open]")?.addEventListener("click", () => priceListImportInput?.click());
    priceListImportInput?.addEventListener("change", () => {
        if (priceListImportInput.files?.length) priceListImportInput.form?.requestSubmit();
    });
    filterPriceList();

    root.addEventListener("click", (event) => {
        const copyButton = event.target.closest("[data-copy-value]");
        if (copyButton) {
            event.preventDefault();
            const value = copyButton.dataset.copyValue || "";
            const markCopied = () => {
                const label = copyButton.querySelector("span");
                if (label) {
                    const previous = label.textContent;
                    label.textContent = "Copied";
                    window.setTimeout(() => { label.textContent = previous || "Copy link"; }, 1600);
                }
            };
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(value).then(markCopied).catch(() => {});
            } else {
                const input = document.createElement("textarea");
                input.value = value;
                input.setAttribute("readonly", "");
                input.style.position = "fixed";
                input.style.opacity = "0";
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                input.remove();
                markCopied();
            }
            return;
        }

        const ajaxLink = event.target.closest("[data-settings-ajax-link]");
        if (ajaxLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
            event.preventDefault();
            refreshRoot(ajaxLink.href).catch(() => {
                window.location.assign(ajaxLink.href);
            });
            return;
        }

        const link = event.target.closest("[data-settings-section-link]");
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();
        setActiveSection(link.dataset.settingsSectionLink);
    });

    const billingModal = root.querySelector("[data-billing-rate-modal]");
    const billingForm = root.querySelector("[data-billing-rate-form]");
    let lastBillingTrigger = null;

    const setBillingField = (name, value) => {
        const field = billingForm?.querySelector(`[data-billing-rate-field="${name}"]`);
        if (!field) {
            return;
        }

        if (field.type === "checkbox") {
            field.checked = value === true || value === "true";
            return;
        }

        field.value = value ?? "";
        if (field.tagName === "SELECT") {
            field.dispatchEvent(new Event("change", { bubbles: true }));
        }
    };

    const closeBillingModal = () => {
        if (!billingModal) {
            return;
        }

        closeRcdDialog(billingModal);
        lastBillingTrigger = null;
    };

    const openBillingModal = (trigger) => {
        if (!billingModal || !billingForm) {
            return;
        }

        lastBillingTrigger = trigger;
        billingForm.reset();
        setBillingField("id", trigger?.dataset.billingRateId || "");
        setBillingField("internal", trigger?.dataset.billingRateInternal || "");
        setBillingField("customer", trigger?.dataset.billingRateCustomer || "");
        setBillingField("vat", trigger?.dataset.billingRateVat || "0");
        setBillingField("novat", trigger?.dataset.billingRateNovat || "0");
        const taxSelect = billingForm.querySelector('[data-billing-rate-field="tax"]');
        setBillingField("tax", trigger?.dataset.billingRateTax || taxSelect?.value || taxSelect?.querySelector("option")?.value || "20");
        setBillingField("includesTax", trigger?.dataset.billingRateIncludesTax || "false");
        setBillingField("default", trigger?.dataset.billingRateDefault || "false");
        setBillingField("archived", trigger?.dataset.billingRateArchived || "false");
        setBillingField("estimated", trigger?.dataset.billingRateEstimated || "");
        setBillingField("account", trigger?.dataset.billingRateAccount || "");
        setBillingField("rounding", trigger?.dataset.billingRateRounding || "");
        setBillingField("minimum", trigger?.dataset.billingRateMinimum || "");

        const archiveRow = billingForm.querySelector("[data-billing-rate-archive-row]");
        if (archiveRow) {
            archiveRow.hidden = !trigger?.dataset.billingRateId;
        }

        const status = billingForm.querySelector("[data-settings-form-status]");
        if (status) {
            status.textContent = "";
            status.removeAttribute("data-state");
        }

        openRcdDialog(billingModal, trigger);
        renderRcdIcons();
    };

    const pricingLevelModal = root.querySelector("[data-pricing-level-modal]");
    const pricingLevelForm = root.querySelector("[data-pricing-level-form]");
    let lastPricingLevelTrigger = null;

    const setPricingLevelField = (name, value) => {
        const field = pricingLevelForm?.querySelector(`[data-pricing-level-field="${name}"]`);
        if (!field) {
            return;
        }

        field.value = value ?? "";
    };

    const closePricingLevelModal = () => {
        if (!pricingLevelModal) {
            return;
        }

        closeRcdDialog(pricingLevelModal);
        lastPricingLevelTrigger = null;
    };

    const openPricingLevelModal = (trigger) => {
        if (!pricingLevelModal || !pricingLevelForm) {
            return;
        }

        lastPricingLevelTrigger = trigger;
        pricingLevelForm.reset();
        setPricingLevelField("id", trigger?.dataset.pricingLevelId || "");
        setPricingLevelField("name", trigger?.dataset.pricingLevelName || "");
        setPricingLevelField("markup", trigger?.dataset.pricingLevelMarkup || "0");

        const status = pricingLevelForm.querySelector("[data-settings-form-status]");
        if (status) {
            status.textContent = "";
            status.removeAttribute("data-state");
        }

        openRcdDialog(pricingLevelModal, trigger);
        renderRcdIcons();
    };

    root.querySelectorAll("[data-pricing-level-tab]").forEach((tabButton) => {
        tabButton.addEventListener("click", () => {
            const target = tabButton.dataset.pricingLevelTab;
            root.querySelectorAll("[data-pricing-level-tab]").forEach((button) => {
                const isActive = button === tabButton;
                button.classList.toggle("is-active", isActive);
                button.setAttribute("aria-selected", isActive ? "true" : "false");
            });
            root.querySelectorAll("[data-pricing-level-row]").forEach((row) => {
                const isArchived = row.dataset.pricingLevelArchived === "true";
                row.hidden = target === "active" ? isArchived : !isArchived;
            });
        });
    });

    root.querySelector("[data-pricing-level-default-select]")?.addEventListener("change", (event) => {
        event.target.closest("[data-pricing-level-default-form]")?.requestSubmit();
    });

    root.querySelectorAll("[data-pricing-level-row]").forEach((row) => {
        row.hidden = row.dataset.pricingLevelArchived === "true";
    });

    const staffModal = root.querySelector("[data-staff-modal]");
    const staffForm = root.querySelector("[data-staff-form]");
    let lastStaffTrigger = null;

    const setStaffField = (name, value) => {
        const field = staffForm?.querySelector(`[data-staff-field="${name}"]`);
        if (!field) {
            return;
        }

        if (field.type === "checkbox") {
            field.checked = value === true || value === "true";
            return;
        }

        field.value = value ?? "";
        if (field.tagName === "SELECT") {
            field.dispatchEvent(new Event("change", { bubbles: true }));
        }
    };

    const closeStaffModal = () => {
        if (!staffModal) {
            return;
        }

        closeRcdDialog(staffModal);
        lastStaffTrigger = null;
    };

    const openStaffModal = (trigger) => {
        if (!staffModal || !staffForm) {
            return;
        }

        lastStaffTrigger = trigger;
        staffForm.reset();
        setStaffField("id", trigger?.dataset.staffId || "");
        setStaffField("name", trigger?.dataset.staffName || "");
        setStaffField("email", trigger?.dataset.staffEmail || "");
        setStaffField("phone", trigger?.dataset.staffPhone || "");
        setStaffField("billingRate", trigger?.dataset.staffBillingRate || staffForm.querySelector('[data-staff-field="billingRate"] option')?.value || "");
        setStaffField("hourlyCost", trigger?.dataset.staffHourlyCost || "0");
        setStaffField("licence", trigger?.dataset.staffLicence || "");
        setStaffField("role", trigger?.dataset.staffRole || "Technician");
        setStaffField("active", trigger?.dataset.staffActive || "true");
        setStaffField("sendInvitation", trigger?.dataset.staffId ? "false" : "true");
        setStaffField("emailNotifications", trigger?.dataset.staffEmailNotifications || "true");
        setStaffField("mobileNotifications", trigger?.dataset.staffMobileNotifications || "true");
        setStaffField("timesheets", trigger?.dataset.staffTimesheets || "true");
        setStaffField("jobs", trigger?.dataset.staffJobs || "true");
        setStaffField("invoicing", trigger?.dataset.staffInvoicing || "false");
        setStaffField("quoting", trigger?.dataset.staffQuoting || "false");
        setStaffField("purchases", trigger?.dataset.staffPurchases || "false");
        setStaffField("reports", trigger?.dataset.staffReports || "false");
        setStaffField("scheduling", trigger?.dataset.staffScheduling || "false");
        setStaffField("enquiries", trigger?.dataset.staffEnquiries || "false");
        setStaffField("tracking", trigger?.dataset.staffTracking || "false");
        setStaffField("settings", trigger?.dataset.staffSettings || "false");
        setStaffField("staffMembers", trigger?.dataset.staffStaffMembers || "false");
        setStaffField("planBilling", trigger?.dataset.staffPlanBilling || "false");

        const avatar = staffForm.querySelector("[data-staff-avatar]");
        const displayName = trigger?.dataset.staffName || "Staff Member";
        if (avatar) {
            avatar.dataset.rcdAvatarKey = displayName;
            avatar.textContent = displayName
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0])
                .join("")
                .toUpperCase() || "SM";
        }

        const status = staffForm.querySelector("[data-settings-form-status]");
        if (status) {
            status.textContent = "";
            status.removeAttribute("data-state");
        }

        openRcdDialog(staffModal, trigger);
        renderRcdIcons();
    };

    const emailTemplateModal = root.querySelector("[data-email-template-modal]");
    const emailTemplateForm = root.querySelector("[data-email-template-form]");
    let lastEmailTemplateTrigger = null;

    const setEmailTemplateField = (name, value) => {
        const field = emailTemplateForm?.querySelector(`[data-email-template-field="${name}"]`);
        if (!field) return;
        if (field.type === "checkbox") {
            field.checked = value === true || value === "true";
        } else {
            field.value = value ?? "";
        }
    };

    const closeEmailTemplateModal = () => {
        if (!emailTemplateModal) return;
        closeRcdDialog(emailTemplateModal);
        lastEmailTemplateTrigger = null;
    };

    const openEmailTemplateModal = (trigger) => {
        if (!emailTemplateModal || !emailTemplateForm) return;
        lastEmailTemplateTrigger = trigger;
        emailTemplateForm.reset();
        setEmailTemplateField("id", trigger.dataset.emailTemplateId || "");
        setEmailTemplateField("key", trigger.dataset.emailTemplateKey || "");
        setEmailTemplateField("name", trigger.dataset.emailTemplateName || "");
        setEmailTemplateField("description", trigger.dataset.emailTemplateDescription || "");
        setEmailTemplateField("subject", trigger.dataset.emailTemplateSubject || "");
        setEmailTemplateField("html", trigger.dataset.emailTemplateHtml || "");
        setEmailTemplateField("text", trigger.dataset.emailTemplateText || "");
        setEmailTemplateField("active", trigger.dataset.emailTemplateActive || "true");
        openRcdDialog(emailTemplateModal, trigger);
        renderRcdIcons();
    };
    root.addEventListener("click", (event) => {
        const openTrigger = event.target.closest("[data-billing-rate-open]");
        if (openTrigger) {
            event.preventDefault();
            openBillingModal(openTrigger);
            return;
        }

        if (event.target.closest("[data-billing-rate-close]") || event.target === billingModal) {
            event.preventDefault();
            closeBillingModal();
        }

        const pricingLevelTrigger = event.target.closest("[data-pricing-level-open]");
        if (pricingLevelTrigger) {
            event.preventDefault();
            openPricingLevelModal(pricingLevelTrigger);
            return;
        }

        if (event.target.closest("[data-pricing-level-close]") || event.target === pricingLevelModal) {
            event.preventDefault();
            closePricingLevelModal();
        }

        const staffTrigger = event.target.closest("[data-staff-open]");
        if (staffTrigger) {
            event.preventDefault();
            openStaffModal(staffTrigger);
            return;
        }

        if (event.target.closest("[data-staff-close]") || event.target === staffModal) {
            event.preventDefault();
            closeStaffModal();
        }
        const emailTemplateTrigger = event.target.closest("[data-email-template-open]");
        if (emailTemplateTrigger) {
            event.preventDefault();
            openEmailTemplateModal(emailTemplateTrigger);
            return;
        }

        if (event.target.closest("[data-email-template-close]") || event.target === emailTemplateModal) {
            event.preventDefault();
            closeEmailTemplateModal();
        }

        const priceListTrigger = event.target.closest("[data-price-list-open]");
        if (priceListTrigger) {
            event.preventDefault();
            openPriceListModal(priceListTrigger);
            return;
        }
        if (event.target.closest("[data-price-list-close]") || event.target === priceListModal) {
            event.preventDefault();
            closePriceListModal();
        }
        const priceListCategoryTrigger = event.target.closest("[data-price-list-category-open]");
        if (priceListCategoryTrigger) {
            event.preventDefault();
            priceListCategoryForm?.reset();
            openRcdDialog(priceListCategoryModal, priceListCategoryTrigger);
            priceListCategoryForm?.querySelector("[data-price-list-category-name]")?.focus();
            return;
        }
        if (event.target.closest("[data-price-list-category-close]") || event.target === priceListCategoryModal) {
            event.preventDefault();
            closePriceListCategoryModal();
        }
    });

    root.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && billingModal && !billingModal.hidden) {
            closeBillingModal();
        }

        if (event.key === "Escape" && staffModal && !staffModal.hidden) {
            closeStaffModal();
        }
        if (event.key === "Escape" && emailTemplateModal && !emailTemplateModal.hidden) {
            closeEmailTemplateModal();
        }
        if (event.key === "Escape" && priceListModal?.open) closePriceListModal();
        if (event.key === "Escape" && priceListCategoryModal?.open) closePriceListCategoryModal();
    });

    root.addEventListener("submit", (event) => {
        const confirmationForm = event.target.closest("[data-price-list-confirm]");
        if (confirmationForm && !window.confirm(confirmationForm.dataset.priceListConfirm || "Continue?")) {
            event.preventDefault();
            return;
        }
        const form = event.target.closest("[data-settings-staff-search], [data-settings-staff-page-size]");
        if (!form) {
            return;
        }

        event.preventDefault();
        refreshRoot(`?${new URLSearchParams(new FormData(form)).toString()}`).catch(() => {
            window.location.assign(`/Settings?${new URLSearchParams(new FormData(form)).toString()}`);
        });
    });

    root.addEventListener("change", (event) => {
        const select = event.target.closest("[data-settings-staff-page-size] select");
        if (!select) {
            return;
        }

        event.preventDefault();
        select.form?.requestSubmit();
    });

    root.addEventListener("submit", async (event) => {
        const form = event.target.closest("[data-settings-ajax-form]");
        if (!form) {
            return;
        }

        event.preventDefault();

        const sectionId = form.dataset.settingsSection || root.dataset.activeSection || "billing-rates";
        const status = form.querySelector("[data-settings-form-status]");
        const buttons = form.querySelectorAll("button, input, select, textarea");
        const payload = new FormData(form);

        buttons.forEach((control) => {
            control.disabled = true;
        });
        form.classList.add("is-saving");
        if (status) {
            status.textContent = "Saving settings...";
            status.dataset.state = "loading";
        }

        try {
            const response = await fetch(form.action || window.location.href, {
                method: form.method || "POST",
                body: payload,
                credentials: "same-origin",
                headers: {
                    "Accept": "application/json",
                    "X-Requested-With": "XMLHttpRequest"
                }
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok || data.success === false) {
                throw new Error(data.detail || data.message || "Settings could not be saved.");
            }

            if (status) {
                status.textContent = data.message || "Settings saved.";
                status.dataset.state = "success";
            }

            window.rcdUser?.toast?.({
                type: "success",
                title: "Settings saved",
                message: data.message || "Workspace settings updated."
            });

            await refreshRoot(data.section || sectionId);
        } catch (error) {
            if (status) {
                status.textContent = error.message || "Settings could not be saved.";
                status.dataset.state = "error";
            }

            window.rcdUser?.toast?.({
                type: "danger",
                title: "Save failed",
                message: error.message || "Please check the settings and try again."
            });
        } finally {
            buttons.forEach((control) => {
                control.disabled = false;
            });
            form.classList.remove("is-saving");
            root.classList.remove("is-loading");
            root.removeAttribute("aria-busy");
        }
    });

    if (window.__rcdSettingsPopstateReady !== true) {
        window.__rcdSettingsPopstateReady = true;
        window.addEventListener("popstate", () => {
            const url = new URL(window.location.href);
            document.querySelector("[data-settings-root]")?.querySelector(`[data-settings-section-link="${url.searchParams.get("section") || "billing-rates"}"]`)?.click();
        });
    }
};

const bootUi = () => {
    renderRcdIcons();
};

window.userApp = () => ({
    ...createRcdThemeState({
        defaultTheme: "light",
        onChange: bootUi
    }),
    sidebarOpen: false,
    logoutLoading: false,
    workspaceSwitching: false,
    init() {
        this.initTheme();

        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                this.sidebarOpen = false;
            }
        });
    },
    async switchWorkspace(tenantId) {
        if (!tenantId || this.workspaceSwitching) {
            return;
        }

        this.workspaceSwitching = true;
        const unblock = window.rcdUser?.blockUi?.({ message: "Switching workspace" });

        try {
            const response = await fetch("/api/auth/select-tenant", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest"
                },
                body: JSON.stringify({ tenantId })
            });

            if (!response.ok) {
                throw new Error("Workspace switch failed");
            }

            window.location.assign("/Dashboard");
        } catch (error) {
            window.rcdUser?.toast?.({
                type: "danger",
                title: "Workspace switch failed",
                message: "Please refresh the page and try again."
            });
            this.workspaceSwitching = false;
            unblock?.();
            renderRcdIcons();
        }
    },
    adminEntryLoading: false,
    async enterAdminPanel() {
        if (this.adminEntryLoading) {
            return;
        }

        this.adminEntryLoading = true;
        const unblock = window.rcdUser?.blockUi?.({ message: "Opening admin panel" });

        try {
            const response = await fetch("/api/auth/select-platform", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "X-Requested-With": "XMLHttpRequest"
                }
            });

            if (!response.ok) {
                throw new Error("Admin entry failed");
            }

            window.location.assign("/Admin/Dashboard");
        } catch (error) {
            window.rcdUser?.toast?.({
                type: "danger",
                title: "Couldn't open admin panel",
                message: "Please refresh the page and try again."
            });
            this.adminEntryLoading = false;
            unblock?.();
            renderRcdIcons();
        }
    },
    async logout() {
        if (this.logoutLoading) {
            return;
        }

        this.logoutLoading = true;
        const messages = window.rcdUserMessages || {};
        const unblock = window.rcdUser?.blockUi?.({ message: messages.logoutSigningOut || "Signing out" });

        try {
            const response = await fetch("/api/auth/logout", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "X-Requested-With": "XMLHttpRequest"
                }
            });

            if (!response.ok && response.status !== 401) {
                throw new Error("Logout failed");
            }

            window.location.assign("/login");
        } catch (error) {
            window.rcdUser?.toast?.({
                type: "danger",
                title: messages.logoutFailedTitle || "Logout failed",
                message: messages.logoutFailedMessage || "Please refresh the page and try again."
            });
            this.logoutLoading = false;
            unblock?.();
            renderRcdIcons();
        }
    }
});

window.priceCatalogBinder = (catalogElementId, fieldMap) => ({
    items: [],
    init() {
        const catalogElement = document.getElementById(catalogElementId);
        if (!catalogElement) {
            return;
        }

        try {
            this.items = JSON.parse(catalogElement.textContent || "[]");
        } catch {
            this.items = [];
        }
    },
    apply(itemId) {
        const item = this.items.find((entry) => entry.id === itemId);
        if (!item) {
            return;
        }

        const description = item.description
            ? `${item.code} - ${item.name}: ${item.description}`
            : `${item.code} - ${item.name}`;
        this.setField(fieldMap.description, description);
        this.setField(fieldMap.unit, item.unit);
        this.setField(fieldMap.amount, fieldMap.mode === "cost" ? item.costPrice : item.sellPrice);
        this.setField(fieldMap.tax, item.taxRate);
    },
    setField(id, value) {
        if (!id) {
            return;
        }

        const field = document.getElementById(id);
        if (!field) {
            return;
        }

        field.value = value ?? "";
        field.dispatchEvent(new Event("input", { bubbles: true }));
        field.dispatchEvent(new Event("change", { bubbles: true }));
    }
});

window.addressLookup = (fieldMap) => ({
    loading: false,
    message: "",
    addresses: [],
    async lookup() {
        const postalCode = this.getFieldValue(fieldMap.postalCodeId);
        if (!postalCode) {
            this.message = "Enter a postcode first.";
            this.addresses = [];
            return;
        }

        this.loading = true;
        this.message = "Looking up address...";
        this.addresses = [];

        try {
            const response = await fetch(`/api/address-lookup/postcode?postalCode=${encodeURIComponent(postalCode)}`, {
                headers: {
                    "Accept": "application/json",
                    "X-Requested-With": "XMLHttpRequest"
                }
            });

            if (!response.ok) {
                throw new Error("Address lookup failed");
            }

            const result = await response.json();
            this.message = result.message || "";
            this.addresses = Array.isArray(result.addresses) ? result.addresses : [];

            if (this.addresses.length === 1) {
                this.apply(0);
            }
        } catch {
            this.message = "Address lookup is unavailable right now.";
            this.addresses = [];
        } finally {
            this.loading = false;
            renderRcdIcons();
        }
    },
    apply(index) {
        const address = this.addresses[Number(index)];
        if (!address) {
            return;
        }

        if (address.line1) {
            this.setFieldValue(fieldMap.addressLine1Id, [address.line1, address.line2].filter(Boolean).join(", "));
        }

        this.setFieldValue(fieldMap.cityId, address.city);
        this.setFieldValue(fieldMap.regionId, address.region);
        this.setFieldValue(fieldMap.postalCodeId, address.postalCode);
        this.setFieldValue(fieldMap.countryId, address.country);
    },
    format(address) {
        return [address.line1, address.line2, address.city, address.postalCode]
            .filter(Boolean)
            .join(", ");
    },
    getFieldValue(id) {
        const field = document.getElementById(id);
        return field?.value?.trim() || "";
    },
    setFieldValue(id, value) {
        if (!id || value == null) {
            return;
        }

        const field = document.getElementById(id);
        if (!field) {
            return;
        }

        field.value = value;
        field.dispatchEvent(new Event("input", { bubbles: true }));
        field.dispatchEvent(new Event("change", { bubbles: true }));
    }
});

window.addressAutocomplete = (options = {}) => ({
    query: "",
    loading: false,
    open: false,
    message: "",
    suggestions: [],
    activeIndex: -1,
    timer: null,
    controller: null,
    input: null,
    suppressNextInput: false,
    endpoint: options.endpoint || "/api/address-lookup/suggest",
    listboxId: `address-suggestions-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`,
    sessionToken: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
    init() {
        this.input = document.getElementById(options.inputId);
        if (!this.input) {
            return;
        }

        this.query = this.input.value || "";
    },
    onInput(event) {
        if (this.suppressNextInput) {
            this.suppressNextInput = false;
            return;
        }

        this.query = event.target.value || "";
        window.clearTimeout(this.timer);

        if (this.query.trim().length < 2) {
            this.close();
            return;
        }

        this.timer = window.setTimeout(() => this.fetchSuggestions(), Number.isFinite(options.delay) ? options.delay : 450);
    },
    async fetchSuggestions() {
        const query = this.query.trim();
        if (query.length < 2) {
            this.close();
            return;
        }

        this.controller?.abort();
        this.controller = new AbortController();
        this.loading = true;
        this.open = true;
        this.message = "Searching addresses...";
        this.suggestions = [];
        this.activeIndex = -1;

        try {
            const url = new URL(this.endpoint, window.location.origin);
            url.searchParams.set("query", query);
            url.searchParams.set("sessionToken", this.sessionToken);
            const response = await fetch(url, {
                signal: this.controller.signal,
                headers: {
                    "Accept": "application/json",
                    "X-Requested-With": "XMLHttpRequest"
                }
            });

            if (!response.ok) {
                throw new Error("Address autocomplete failed");
            }

            const result = await response.json();
            this.suggestions = Array.isArray(result.suggestions) ? result.suggestions : [];
            this.message = this.suggestions.length > 0 ? "" : (result.message || "No matching addresses found.");
            this.open = this.suggestions.length > 0 || Boolean(this.message);
        } catch (error) {
            if (error.name !== "AbortError") {
                this.message = "Address suggestions are unavailable right now.";
                this.suggestions = [];
                this.open = true;
            }
        } finally {
            this.loading = false;
            renderRcdIcons();
        }
    },
    move(direction) {
        if (!this.open || this.suggestions.length === 0) {
            return;
        }

        this.activeIndex = (this.activeIndex + direction + this.suggestions.length) % this.suggestions.length;
    },
    chooseActive() {
        if (this.activeIndex < 0) {
            return;
        }

        this.choose(this.suggestions[this.activeIndex]);
    },
    choose(suggestion) {
        const formattedAddress = suggestion?.formattedAddress;
        if (!formattedAddress || !this.input) {
            return;
        }

        this.input.value = formattedAddress;
        this.query = formattedAddress;
        this.suppressNextInput = true;
        this.input.dispatchEvent(new Event("input", { bubbles: true }));
        this.input.dispatchEvent(new Event("change", { bubbles: true }));
        this.close();
    },
    close() {
        this.open = false;
        this.loading = false;
        this.message = "";
        this.suggestions = [];
        this.activeIndex = -1;
        this.controller?.abort();
    }
});

export { setupWorkspaceSettingsPage };
